import { useTheme } from '@emotion/react'
import {
    AddToCartEvent,
    AppState,
    AuthenticationState,
    BreachEvent,
    BreachScreenType,
    click_origin_breach_screen,
    DataLayerEventName,
    ViewSubscriptionItem,
} from '@news-mono/web-common'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { getAttribution } from '../../../../../errors/ErrorPage'
import { useAuthActions } from '../../../../authentication'
import { createLinkClickEvent } from '../../../createLinkClickHandler'
import {
    everydayDigitalClassName,
    StyledBreachContainer,
    StyledBreachFooter,
    StyledContactDetails,
    StyledDayRange,
    StyledHeading,
    StyledLogInButton,
    StyledLoginContainer,
    StyledOutlineAnchor,
    StyledParagraph,
    StyledParagraphAboveMobile,
    StyledParagraphMobileOnly,
    StyledPlusIcon,
    StyledPremiumLabel,
    StyledSubcriptionSubtitle,
    StyledSubcriptionText,
    StyledSubscribeButton,
    StyledSubscription,
    StyledSubscriptionContainer,
    StyledSubscriptionHeading,
    StyledSubscriptionInfo,
    StyledSubscriptionInfoContainer,
    StyledSubscriptionWrapper,
    StyledWAFootyLogo,
    ultimatePackClassName,
} from './SportBreachNotification.styled'
import { breachPackages } from '../../../common'
import { getCartPayload, toItemListId } from '../../../../../'

const plusIcon = require('./assets/plus.png')

export interface SportBreachNotificationProps {
    breachCreativeName: string
    breachDeal: string
    breachType: string
    onEvent: (event: BreachEvent) => void
}

export function SportBreachNotification({
    breachDeal,
    breachCreativeName,
    breachType,
    onEvent,
}: SportBreachNotificationProps) {
    const { isLoggedIn } = useSelector<AppState, AuthenticationState>(
        (state) => state.authentication,
    )
    const { onSubscribeClick, onLoginClick } = useAuthActions()
    const breachScreenType: BreachScreenType = 'Premium - Inline'

    const theme = useTheme()
    const masthead =
        getAttribution(theme.kind, theme.section) || 'The West Australian'

    const packages = breachPackages.sport
        .map(pkg => getCartPayload(pkg, masthead))
        .filter(payload => payload !== undefined) as AddToCartEvent['payload'][]

    useEffect(() => {
        const viewSubscriptionItems: ViewSubscriptionItem[] = []

        // Iterate over the packages in the breach screen
        for (let index = 0; index < packages.length; index++) {
            const sportPkg = packages[index];

            // Iterate over the items in the package
            for (let pkgIndex = 0; pkgIndex < sportPkg.items.length; pkgIndex++) {
                const cartItem = sportPkg.items[pkgIndex];

                viewSubscriptionItems.fill({
                    index,
                    ...cartItem,
                    component_path: `offercheck/thewest?redirect=true&offerCode=${cartItem.coupon}`,
                    item_list_category: 'freemium',
                    item_list_category2: breachScreenType,
                    item_list_name: breachCreativeName,
                    item_list_id: toItemListId(breachCreativeName),
                    deal_name: breachType,
                    currency: sportPkg.currency
                });
            }
        }

        onEvent({
            type: DataLayerEventName.viewSubscriptionItemListEvent,
            parentType: DataLayerEventName.viewItemListEvent,
            originator: 'breachscreen',
            payload: { items: viewSubscriptionItems },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <StyledBreachContainer data-testid="SportBreachScreen">
            <StyledHeading>Subscribe to the West Australian</StyledHeading>
            <StyledPremiumLabel>
                This article is available to subscribers who have digital access
                included in their subscription.
                {!isLoggedIn && (
                    <StyledLoginContainer>
                        {' '}
                        <StyledLogInButton onClick={onLoginClick}>
                            Log in
                        </StyledLogInButton>
                    </StyledLoginContainer>
                )}
            </StyledPremiumLabel>
            <StyledWAFootyLogo />
            <StyledSubcriptionSubtitle>
                <StyledSubcriptionText>
                    Don’t miss the latest footy news!
                </StyledSubcriptionText>
            </StyledSubcriptionSubtitle>
            <StyledSubscriptionContainer>
                <StyledSubscription className={ultimatePackClassName}>
                    <StyledSubscriptionWrapper>
                        <StyledSubscriptionHeading>
                            The Footy Pack
                        </StyledSubscriptionHeading>
                        <StyledSubscriptionInfoContainer>
                            <StyledSubscriptionInfo bold>
                                Digital + Print
                            </StyledSubscriptionInfo>
                        </StyledSubscriptionInfoContainer>
                        <StyledSubscriptionInfoContainer>
                            <StyledSubscriptionInfo strikethrough>
                                $13
                            </StyledSubscriptionInfo>{' '}
                            <StyledSubscriptionInfo bold>
                                $7.50
                            </StyledSubscriptionInfo>{' '}
                            <StyledSubscriptionInfo>
                                per week for 8 weeks*
                            </StyledSubscriptionInfo>
                        </StyledSubscriptionInfoContainer>
                        <StyledSubscribeButton
                            onClick={() => {
                                createLinkClickEvent(
                                    breachCreativeName,
                                    breachDeal,
                                    onEvent,
                                    undefined,
                                    1,
                                    packages[0],
                                )()
                                onSubscribeClick({
                                    offerCode: 'FOOTYPAK',
                                    callToAction: click_origin_breach_screen,
                                    breachScreenType: breachScreenType,
                                })
                            }}
                        >
                            Subscribe Now
                        </StyledSubscribeButton>
                        <StyledParagraph>
                            Enjoy unlimited access to thewest.com.au and
                            everyday digital editions on any device.
                        </StyledParagraph>
                        <StyledDayRange>
                            <StyledParagraph marginTop bold>
                                <StyledPlusIcon src={plusIcon} alt="" />
                                Thursday – Monday
                            </StyledParagraph>
                        </StyledDayRange>
                        <StyledParagraphAboveMobile bold>
                            Home delivery, with the latest footy news, new look
                            Pre-Game and The Game lift outs with fixtures,
                            highlights and expert opinions from WA’s leading
                            football writers.
                        </StyledParagraphAboveMobile>
                        <StyledParagraphMobileOnly bold>
                            Papers home delivered with all the latest footy
                            news!
                        </StyledParagraphMobileOnly>
                        <StyledParagraph marginTop>*T&Cs apply</StyledParagraph>
                    </StyledSubscriptionWrapper>
                </StyledSubscription>
                <StyledSubscription className={everydayDigitalClassName}>
                    <StyledSubscriptionWrapper>
                        <StyledSubscriptionHeading>
                            Everyday Digital
                        </StyledSubscriptionHeading>
                        <StyledSubscriptionInfoContainer>
                            <StyledSubscriptionInfo bold>
                                Digital only
                            </StyledSubscriptionInfo>
                        </StyledSubscriptionInfoContainer>
                        <StyledSubscriptionInfoContainer>
                            <StyledSubscriptionInfo bold>
                                $3.50
                            </StyledSubscriptionInfo>{' '}
                            <StyledSubscriptionInfo>
                                per week for 8 weeks*
                            </StyledSubscriptionInfo>
                        </StyledSubscriptionInfoContainer>
                        <StyledSubscribeButton
                            onClick={() => {
                                createLinkClickEvent(
                                    breachCreativeName,
                                    breachDeal,
                                    onEvent,
                                    undefined,
                                    1,
                                    packages[1],
                                )()
                                onSubscribeClick({
                                    offerCode: 'DIG50',
                                    callToAction: click_origin_breach_screen,
                                    breachScreenType: breachScreenType,
                                })
                            }}
                        >
                            Subscribe Now
                        </StyledSubscribeButton>
                        <StyledParagraph>
                            Enjoy unlimited access to thewest.com.au PLUS daily
                            digital editions of The West Australian and The
                            Sunday Times on any device!
                        </StyledParagraph>
                    </StyledSubscriptionWrapper>
                </StyledSubscription>
            </StyledSubscriptionContainer>
            <StyledBreachFooter>
                {/* <StyledBreachFooterText></StyledBreachFooterText> */}
                <StyledContactDetails>
                    Need Help? Call <a href="tel:1800 811 855">1800 811 855</a>{' '}
                    or view{' '}
                    <StyledOutlineAnchor
                        onClick={() => {
                            createLinkClickEvent(
                                breachCreativeName,
                                breachDeal,
                                onEvent,
                            )()
                            onSubscribeClick({
                                callToAction: click_origin_breach_screen,
                                breachScreenType: breachScreenType,
                            })
                        }}
                        tabIndex={0}
                    >
                        all subscription packages
                    </StyledOutlineAnchor>
                </StyledContactDetails>
            </StyledBreachFooter>
        </StyledBreachContainer>
    )
}
